import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import type { LoaderDataWithOverrideLocale } from '#/horizon/useMatchesOverrideLocale';
import { createI18nFn } from '#/i18n/createI18nFn';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiIsomorphicFetchCanSubmitSurvey } from '#/modules/salary/api/salaryApiFetchCanSubmitSurvey';
import {
  salariesExploreRoute,
  salariesSurveyRoute,
} from '#/modules/salary/routing/salary.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SalarySurveyPage } from './SalarySurveyPage';

export const handle = {
  variant: 'base',
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof salariesSurveyRoute>) {
  const canSubmitSurvey = await salaryApiIsomorphicFetchCanSubmitSurvey({
    request,
    context,
  })().catch(() => false);

  if (!canSubmitSurvey) {
    const i18n = createI18nFn(context.locale);

    return idealistRedirect({
      request,
      context,
      to: salariesExploreRoute.with({}),
      flash: i18n(
        'We already have your survey information, but we love your enthusiasm!',
      ),
    });
  }

  return {
    overrideLocale: I18nLocale.EN,
  } satisfies LoaderDataWithOverrideLocale;
}

export default function SalarySurveyHorizonRoute(
  _: HorizonAppRouteComponentProps<typeof loader>,
) {
  const { appConfig } = useAppConfig();

  return (
    <RouteModule
      title="Idealist"
      ogImage={getStaticImageSrc(
        'compdb/og-image.png',
        appConfig.idealist.staticImgPath,
      )}
    >
      <SalarySurveyPage />
    </RouteModule>
  );
}
